import { useEffect, useState } from "react";
import { AddOnTextInput, Badge, Button, Modal, Typography } from "@/components/atoms";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import { useGetSubscriptionPlansQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { clsx } from "@/utils";

interface CalculateMyPriceModalProps {
  open: boolean;
  onClose: () => void;
  initialMonthlyBookings?: number;
}

export const CalculateMyPriceModal = ({ open, onClose, initialMonthlyBookings = 0 }: CalculateMyPriceModalProps) => {
  const [monthlyBookings, setMonthlyBookings] = useState(String(initialMonthlyBookings));

  // Update monthlyBookings when modal opens or initialMonthlyBookings changes
  useEffect(() => {
    if (open) {
      setMonthlyBookings(String(initialMonthlyBookings));
    }
  }, [open, initialMonthlyBookings]);
  const { data: plansData, isLoading } = useGetSubscriptionPlansQuery();

  const plans = plansData?.subscriptions ?? [];

  // Calculate total monthly cost: subscription fee + (bookings * platform fee per ride)
  const calculateTotalMonthlyCost = (subscriptionFeeInCents: number, platformFeePerRideInCents: number) => {
    const bookingsNum = Number(monthlyBookings) || 0;
    return (subscriptionFeeInCents + bookingsNum * platformFeePerRideInCents) / 100;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Calculate My Price"
      description="Enter your estimated monthly trips to see a transparent price. Pay only for what you see."
      variant="web-large"
      className="!max-w-[800px]"
    >
      <div className="flex flex-col gap-6">
        {/* Monthly Bookings Input */}
        <div className="flex items-center gap-4">
          <AddOnTextInput
            type="number"
            value={monthlyBookings}
            onChange={(e) => setMonthlyBookings(e.target.value)}
            startAddOn="Monthly Bookings"
            className="max-w-xs"
            min={0}
          />
        </div>

        {/* Plans Grid */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Typography variant="paragraph">Loading plans...</Typography>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {plans.map((plan) => {
              const totalMonthlyCost = calculateTotalMonthlyCost(plan.subscriptionFee, plan.platformFeePerRide);
              const disabledPlanInfo = getDisabledPlanInfo(plan.slug);
              const isPlanDisabled = disabledPlanInfo.disabled;

              return (
                <div
                  key={plan.subscriptionId}
                  className={clsx(
                    "flex flex-col rounded-lg border bg-neutral-surface-gray",
                    "px-4 py-4",
                    "border-neutral-mid-gray",
                    isPlanDisabled && "opacity-50"
                  )}
                >
                  {/* Logo and Plan Name */}
                  <div className="mb-2.5 flex items-center gap-3">
                    {plan.iconSvg && (
                      <div
                        className="flex h-6 w-6 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full"
                        dangerouslySetInnerHTML={{ __html: plan.iconSvg }}
                      />
                    )}
                    <Typography variant="title" className="text-neutral-dark-gray">
                      {plan.subscriptionName}
                    </Typography>
                    {isPlanDisabled && <Badge type="disabled" message={disabledPlanInfo.reason || ""} size="sm" />}
                  </div>

                  {/* Total Monthly Cost */}
                  <Typography variant="h1" className="mb-2 text-neutral-black">
                    ${totalMonthlyCost.toFixed(2)} <span className="text-lg font-normal">/mo.</span>
                  </Typography>

                  {/* Per Trip Cost */}
                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    Per Trip Cost: ${(plan.platformFeePerRide / 100).toFixed(2)} /trip
                  </Typography>

                  {/* Plan Base Fee */}
                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    {plan.subscriptionName} Plan: ${(plan.subscriptionFee / 100).toFixed(2)} /month
                  </Typography>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
